import { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Check, X, Clock, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_META = {
  upcoming: { label: "Upcoming", color: "var(--cream-dim)", chip: "chip", icon: Clock },
  awaiting: { label: "Awaiting", color: "var(--warn)", chip: "chip warn", icon: Clock },
  on_time: { label: "On time", color: "var(--success)", chip: "chip success", icon: Check },
  late_recovered: { label: "Late / recovered", color: "var(--warn)", chip: "chip warn", icon: AlertTriangle },
  missed: { label: "Missed", color: "var(--danger)", chip: "chip danger", icon: X },
};

export default function CommitmentTracker() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      setRows(await endpoints.commitments({}));
    } catch {
      toast.error("Failed to load commitments");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const DUE_STATUSES = ["on_time", "late_recovered", "missed"];

  const counts = useMemo(() => {
    const c = { all: 0, upcoming: 0, awaiting: 0, due: 0, on_time: 0, late_recovered: 0, missed: 0 };
    rows.forEach((r) => {
      c.all += 1;
      c[r.status] = (c[r.status] || 0) + 1;
      if (DUE_STATUSES.includes(r.status)) c.due += 1;
    });
    return c;
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (status === "all") return rows;
    if (status === "due") return rows.filter((r) => DUE_STATUSES.includes(r.status));
    return rows.filter((r) => r.status === status);
  }, [rows, status]);

  const mark = async (row, isLate) => {
    const sched = new Date(row.scheduled_date);
    const activityDate = new Date(sched);
    if (isLate) activityDate.setDate(activityDate.getDate() + 5);
    try {
      await endpoints.markCommitment(row.id, { activity_date: activityDate.toISOString().slice(0, 10) });
      toast.success("Commitment recorded");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const reset = async (row) => {
    try {
      await endpoints.resetCommitment(row.id);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="container-hi py-10" data-testid="commitments-page">
      <header>
        <div className="text-[11px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
          Commitment Tracker
        </div>
        <h1 className="font-display text-[44px] leading-[1.05] mt-2" style={{ color: "var(--cream)" }}>
          Every scheduled window
        </h1>
        <p className="mt-3 text-[13px] max-w-2xl" style={{ color: "var(--cream-dim)" }}>
          A commitment counts as <span style={{ color: "var(--gold-light)" }}>on time</span> when the underlying
          activity date falls within ±3 calendar days of the scheduled date. Late contributions still improve
          savings but remain identified as late/recovered — they do not retroactively become on-time.
        </p>
      </header>

      <div className="mt-8 flex flex-wrap gap-1 p-1 rounded-md w-fit" style={{ background: "var(--surface)", border: "1px solid var(--hairline)" }}>
        {[
          { k: "all", label: "All" },
          { k: "upcoming", label: "Upcoming" },
          { k: "awaiting", label: "Awaiting" },
          { k: "due", label: "Due" },
          { k: "on_time", label: "On time" },
          { k: "late_recovered", label: "Late/Recovered" },
          { k: "missed", label: "Missed" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`commit-filter-${f.k}`}
            onClick={() => setStatus(f.k)}
            className="px-3 py-1.5 text-[12px] rounded"
            style={{
              background: status === f.k ? "var(--gold)" : "transparent",
              color: status === f.k ? "var(--obsidian)" : "var(--cream-dim)",
            }}
          >
            {f.label} <span className="font-mono-num opacity-70">{counts[f.k] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 surface overflow-hidden" data-testid="commitments-table">
        <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-[0.15em] font-mono-num border-b" style={{ color: "var(--cream-muted)", borderColor: "var(--hairline)" }}>
          <div className="col-span-2">Scheduled</div>
          <div className="col-span-3">Participant</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Activity date</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && (
          <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>Loading…</div>
        )}
        {!loading && visibleRows.length === 0 && (
          <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>No commitments match.</div>
        )}
        {visibleRows.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <div key={r.id} className="grid grid-cols-12 px-6 py-3 items-center border-b text-[13px]" style={{ borderColor: "var(--hairline)" }} data-testid={`commit-row-${r.id}`}>
              <div className="col-span-2 font-mono-num" style={{ color: "var(--cream)" }}>{fmtDate(r.scheduled_date)}</div>
              <div className="col-span-3 min-w-0">
                <Link to={`/participants/${r.participant_id}`} className="hover:underline" style={{ color: "var(--cream)" }}>{r.participant_name}</Link>
                <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>{r.trip_destination}</div>
              </div>
              <div className="col-span-2 font-mono-num" style={{ color: "var(--cream)" }}>{fmtMoney(r.amount)}</div>
              <div className="col-span-2 font-mono-num" style={{ color: "var(--cream-dim)" }}>{r.activity_date ? fmtDate(r.activity_date) : "—"}</div>
              <div className="col-span-1">
                <span className={meta.chip}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                {r.status === "on_time" || r.status === "late_recovered" ? (
                  <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => reset(r)} data-testid={`ct-reset-${r.id}`}>
                    <RotateCcw className="w-3 h-3 inline mr-1" /> Reset
                  </button>
                ) : (
                  <>
                    <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => mark(r, false)} data-testid={`ct-mark-ontime-${r.id}`}>On time</button>
                    <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => mark(r, true)} data-testid={`ct-mark-late-${r.id}`}>Late</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
